import {
	getVariantPrice,
	moneyToNumber,
} from "@babybjorn/utils/commercetools/money";
import type { Cart, LineItem } from "@commercetools/platform-sdk";
import { useLocation } from "@reach/router";
import type { TCountryCode } from "countries-list";
import { useEffect, useRef } from "react";
import { useAppState } from "../context/app/context";
import { formatMoney } from "../utils/cart/currency";
import {
	hasCookieConsent,
	useHasCookieConsent,
} from "../utils/cookies/cookie-consent-service";
import { useCartService } from "./use-cart-service";
import useScript from "./use-script";
import {
	useStoryblokProductVariants,
	type StoryblokProductVariants,
} from "./use-storyblok-product-variant";
import { useTranslatedPaths } from "./use-translated-paths";

const klavioItems = (items: LineItem[]) =>
	items.map((item) => {
		const language = process.env.GATSBY_LANGUAGE;
		if (!language) throw new Error("Language not set");

		const price = moneyToNumber(item.price.value);

		return {
			ProductName: item.name[language] ?? "",
			ProductID: `${item.productId.toString()}_${item.variant.id}_${
				process.env.GATSBY_LANGUAGE?.split("-")[0] ?? ""
			}-${(process.env.GATSBY_STORE ?? "").toUpperCase()}`,
			SKU: item.variant.sku ?? "",
			Price: price,
			Quantity: item.quantity,
			Category: item.productType.obj?.name ?? "",
			TotalPrice: item.quantity > 1 ? price * item.quantity : undefined,
		};
	});

const viewedProductKlaviyo = ({
	name,
	productVariant,
	url,
	imageUrl,
	category,
	currency,
	country,
}: {
	name: string;
	productVariant: Queries.ProductVariantFragment;
	url: string;
	imageUrl: string;
	category: string;
	currency: string;
	country?: TCountryCode;
}) => {
	if (window.klaviyo && hasCookieConsent("targeting")) {
		const price = getVariantPrice(productVariant, currency, country);

		const item = {
			ProductName: name,
			ProductID: `${productVariant.id.toString()}_${
				process.env.GATSBY_LANGUAGE?.split("-")[0] ?? ""
			}-${(process.env.GATSBY_STORE ?? "").toUpperCase()}`,
			SKU: productVariant.sku ?? "",
			ImageURL: imageUrl,
			URL: url,
			Price: formatMoney(price),
			Category: category,
		};
		window.klaviyo.push(["track", "Viewed Product", item]);
	}
};

const startedCheckout = (cart: Cart, checkoutPath: string) => {
	if (window.klaviyo && hasCookieConsent("targeting")) {
		const items = klavioItems(cart.lineItems);

		window.klaviyo.push([
			"track",
			"Started Checkout",
			{
				event_id: cart.id,
				value: formatMoney(cart.totalPrice),
				ItemNames: items.map((item) => item.ProductName),
				CheckoutURL: checkoutPath,
				Categories: [...new Set(items.map((item) => item.Category))],
				country: cart.country ?? "",
				locale: cart.locale ?? "",
				store: cart.store?.key ?? "",
				Items: items,
			},
		]);
	}
};

const useKlaviyo = () => {
	const { cart, cartLoading } = useCartService();
	const oldCartRef = useRef<Cart | undefined>(cart);
	const location = useLocation();
	const { checkoutPath } = useTranslatedPaths();

	useScript({
		script: {
			id: "klaviyo_script",
			src: `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.GATSBY_KLAVIYO_COMPANY_ID}`,
			strategy: "idle",
			defer: true,
		},
		removeOnUnmount: false,
		condition: "targeting",
		shouldRender: !!process.env.GATSBY_KLAVIYO_COMPANY_ID,
	});

	const getChangedLineItem = (
		newCart: Cart,
		oldCart?: Cart,
	): LineItem | null => {
		for (const newItem of newCart.lineItems) {
			const oldItem =
				oldCart && oldCart.lineItems.find((item) => item.id === newItem.id);

			if (!oldItem || oldItem.quantity !== newItem.quantity) {
				return newItem;
			}
		}

		return null;
	};

	const sbProducts = useStoryblokProductVariants(
		cart?.lineItems
			.map((item) => item.variant.sku)
			.filter((sku): sku is string => !!sku) ?? [],
	);

	const hasCookieConsent = useHasCookieConsent("targeting");

	useEffect(() => {
		if (!cartLoading && cart && hasCookieConsent) {
			if (
				cart.totalLineItemQuantity !== oldCartRef.current?.totalLineItemQuantity
			) {
				const addedItem = getChangedLineItem(cart, oldCartRef.current);
				addedToCart(cart, sbProducts, addedItem);
			}

			if (location.pathname.includes("checkout")) {
				startedCheckout(cart, checkoutPath);
			}
		}
		oldCartRef.current = cart;
	}, [cart, cartLoading, sbProducts, location, checkoutPath, hasCookieConsent]);
};

const addedToCart = (
	cart: Cart,
	sbProducts: Partial<StoryblokProductVariants>,
	addedItem?: LineItem | null,
) => {
	if (window.klaviyo) {
		const items = klavioItems(cart.lineItems);

		const addWindowLocationOrigin = (url: string) =>
			`${window.location.origin}/${url}`;

		const getUrlAndImage = (sku?: string) => {
			if (!sku) return { url: undefined, image: undefined };
			const sbProduct = sbProducts[sku];

			return {
				url: sbProduct?.path && addWindowLocationOrigin(sbProduct.path),
				image: sbProduct?.images?.[1],
			};
		};

		window.klaviyo.push([
			"track",
			"Added to Cart",
			{
				$value: moneyToNumber(cart.totalPrice),
				country: cart.country ?? "",
				locale: cart.locale ?? "",
				store: cart.store?.key ?? "",
				AddedItemProductName: addedItem?.name[cart.locale ?? "en"],
				AddedItemProductID:
					addedItem?.productId && addedItem.variant.id
						? `${addedItem.productId.toString()}_${addedItem.variant.id}_${
								process.env.GATSBY_LANGUAGE?.split("-")[0] ?? ""
							}-${(process.env.GATSBY_STORE ?? "").toUpperCase()}`
						: undefined,
				AddedItemSKU: addedItem?.variant.sku ?? undefined,
				AddedItemCategories: addedItem?.productType.obj?.name
					? [addedItem.productType.obj.name]
					: undefined,
				AddedItemImageURL: getUrlAndImage(addedItem?.variant.sku).image
					?.filename,
				AddedItemURL: getUrlAndImage(addedItem?.variant.sku).url,
				AddedItemPrice: moneyToNumber(addedItem?.price.value),
				AddedItemQuantity: addedItem ? 1 : undefined,
				ItemNames: cart.lineItems.map(
					(product) => product.name[cart.locale ?? "en"] ?? "",
				),
				CheckoutURL: addWindowLocationOrigin("checkout"),
				Items: items,
			},
		]);
	}
};

export const useKlaviyoViewedProduct = (
	product: Queries.CommerceToolsProductFragment,
	variant: Queries.ProductVariantFragment,
	imageUrl: string,
) => {
	const previousViewedItemId = useRef<string | null>(null);
	const { siteConfig, userCountry } = useAppState();

	useEffect(() => {
		if (
			previousViewedItemId.current !== variant.id &&
			hasCookieConsent("targeting")
		) {
			viewedProductKlaviyo({
				name: product.name,
				productVariant: variant,
				url: window.location.href,
				imageUrl: imageUrl,
				category: product.categories[0]?.category?.name ?? "",
				country: userCountry,
				currency: siteConfig.currency,
			});
		}
		previousViewedItemId.current = variant.id;
	}, [product, variant, imageUrl, userCountry, siteConfig.currency]);
};

export default useKlaviyo;
